import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { FormProvider, useForm, yupResolver } from 'FrontRoyalReactHookForm';
import { type Place, type PlaceDetails } from 'GooglePlaces';
import { type CurrentUser } from 'Users';
import { Button } from 'Button';
import { Text } from 'Text';
import { LocationSearch } from 'LocationSearch';
import { useSaveDocuments } from '../useSaveDocuments';
import { addressFromPlaceDetails } from '../addressFromPlaceDetails';
import { MAX_ADDRESS_LINE_LENGTH, US_COUNTRY_CODE } from '../constants';
import { type MailingAddressFormValues } from '../editDocuments.types';
import { MailingAddressFields } from './MailingAddressFields';

// PhoneInput only ever stores a valid E.164 string or null, so `required` is what blocks an invalid
// number. This shape check guards the value the server supplied.
const E164 = /^\+[1-9]\d{6,}$/;

function buildSchema() {
    const requiredForUs = (fieldSchema: Yup.StringSchema) =>
        fieldSchema.when('country', { is: US_COUNTRY_CODE, then: schema => schema.required() });

    return Yup.object({
        address_line_1: Yup.string().trim().required().max(MAX_ADDRESS_LINE_LENGTH),
        address_line_2: Yup.string().trim().max(MAX_ADDRESS_LINE_LENGTH),
        country: Yup.string().trim().required(),
        city: requiredForUs(Yup.string().trim()),
        state: requiredForUs(Yup.string().trim()),
        zip: requiredForUs(Yup.string().trim()),
        phone: Yup.string().nullable().required().matches(E164),
    });
}

// Built once: the schema closes over module constants only, and useForm re-reads its options
// object on every render, so rebuilding it per keystroke was pure waste.
const SCHEMA = buildSchema();

// Mailing address form. Keeps the old flow: the fields start locked behind a Google Places address
// search unless the user already has an address line 1.
export function MailingAddressSection({ currentUser }: { currentUser: CurrentUser }) {
    const { t } = useTranslation('back_royal');
    const { save, saving } = useSaveDocuments();

    // Locked until the user searches for an address, unless they have already provided one.
    const [formIsCollapsed, setFormIsCollapsed] = useState(() => !currentUser.addressLine1);
    // Only sent to the server when the user actually picks a place this session — see onSave.
    const [chosenPlace, setChosenPlace] = useState<{ placeId: string; placeDetails: PlaceDetails } | null>(null);

    const methods = useForm<MailingAddressFormValues>({
        resolver: yupResolver(SCHEMA),
        mode: 'onChange',
        defaultValues: {
            address_line_1: currentUser.addressLine1 ?? '',
            address_line_2: currentUser.addressLine2 ?? '',
            city: currentUser.city ?? '',
            state: currentUser.state ?? '',
            zip: currentUser.zip ?? '',
            country: currentUser.country ?? '',
            phone: currentUser.phone ?? null,
        },
    });
    const { isDirty, isValid } = methods.formState;

    // Populate the address fields from the chosen place and unlock the form, mirroring
    // `onPlaceChange` in the old directive.
    // Depends on setFieldValue rather than `methods`, which is a new object on every render (and so
    // every keystroke), churning the Google Places listener this is handed to.
    const { setFieldValue } = methods;
    const onPlaceChanged = useCallback(
        (place: Place, placeDetails: PlaceDetails) => {
            Object.entries(addressFromPlaceDetails(placeDetails)).forEach(([field, value]) => {
                setFieldValue(field as keyof MailingAddressFormValues, value);
            });
            setChosenPlace({ placeId: place.place_id, placeDetails });
            setFormIsCollapsed(false);
        },
        [setFieldValue],
    );

    const onSave = async (values: MailingAddressFormValues) => {
        // Empty optional fields go back as null, not ''. `missing_address?` tests truthiness, and ''
        // is truthy in Ruby, so a blank city/state/zip would count as provided.
        const fields: Record<string, unknown> = Object.fromEntries(
            Object.entries(values).map(([field, value]) => [field, value === '' ? null : value]),
        );

        // The chosen Google Place is persisted through the career profile: perform_update calls
        // process_career_profile_updates for learners too, and that cherry-picks exactly
        // {id, place_id, place_details}. Only send it when a place was actually picked this session —
        // sending nulls would wipe the user's existing place.
        if (chosenPlace) {
            fields.career_profile = {
                id: currentUser.careerProfile.id,
                place_id: chosenPlace.placeId,
                place_details: chosenPlace.placeDetails,
            };
        }

        // The address fields and phone are not echoed back by set_user_push_messages, so sync them.
        const saved = await save({ fields, successKey: 'mailing_address_saved', syncToCurrentUser: true });
        if (saved) methods.reset(values);
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSave)} className="flex flex-col gap-4 py-6">
                <Text as="h2" color="black" weight="semibold" className="text-base">
                    {t('settings.edit_documents.mailing_address')}
                </Text>
                <Text as="p" variant="small">
                    {t('settings.edit_documents.mailing_address_description')}
                </Text>

                <div className="flex max-w-md flex-col gap-1">
                    {/* The icon was baked into the locale string as markup in Angular; it belongs in
                        the JSX now, but it stays on the header for parity with main. */}
                    <Text as="div" color="black" weight="semibold" className="text-sm">
                        <FontAwesomeIcon icon={faMagnifyingGlass} className="me-2" />
                        {t('settings.edit_documents.address')}
                    </Text>
                    <Text as="p" variant="small">
                        {t('settings.edit_documents.address_help_text')}
                    </Text>

                    {/* Not part of the form; it only drives the autocomplete. */}
                    <LocationSearch
                        scope="addresses"
                        aria-label={t('settings.edit_documents.address')}
                        placeholder={t('settings.edit_documents.address_placeholder')}
                        onPlaceChanged={onPlaceChanged}
                    />
                </div>

                {/* Unmounted rather than CSS-hidden while collapsed, so the locked fields aren't
                    keyboard tab stops. react-hook-form keeps values for unmounted fields
                    (shouldUnregister defaults to false), so anything populated from a chosen place
                    survives this and is still submitted. */}
                {formIsCollapsed ? null : (
                    <div className="flex max-w-md flex-col gap-4">
                        <MailingAddressFields initialShowAddressLine2={!!currentUser.addressLine2} />

                        <Button
                            type="submit"
                            color="primary"
                            height={9}
                            fullWidth
                            isDisabled={saving || !isDirty || !isValid}
                        >
                            {t('settings.edit_documents.save_mailing_address')}
                        </Button>
                    </div>
                )}
            </form>
        </FormProvider>
    );
}
